import React from "react";
import { Navigate } from "react-router-dom";
import { useSubdomain } from "../../providers/SubdomainProvider";

/**
 * Default logged-in page per subdomain.
 * Used as the redirect target when a user hits a route
 * that isn't available on their current subdomain.
 */
export const SUBDOMAIN_DEFAULTS = {
  app: "/dashboard",
  platform: "/chat",
  clinic: "/clinic",
};

/**
 * SubdomainRoute - Route guard that restricts access by subdomain.
 *
 * This is a UX boundary, not a security boundary.
 * Security is enforced by the permission system (PermissionRoute).
 *
 * Props:
 *   allow     - "app" | "platform" (which subdomain can see this route)
 *   component - the page component to render
 *
 * Behavior:
 *   - If current subdomain matches `allow` -> render component
 *   - If subdomain is null (bare localhost) -> allow everything (dev convenience)
 *   - If wrong subdomain -> redirect to that subdomain's default page
 *
 * Usage:
 *   <SubdomainRoute allow="app" component={PreScreenPage} />
 *   <SubdomainRoute allow="platform" component={TeamManagementPage} />
 */
export const SubdomainRoute = ({ allow, component: Component }) => {
  const { subdomain } = useSubdomain();

  // Bare localhost (no subdomain) → allow everything for dev convenience
  if (subdomain === null) {
    return <Component />;
  }

  // Correct subdomain → render the component
  if (subdomain === allow) {
    return <Component />;
  }

  // Wrong subdomain → redirect to the current subdomain's default page
  const redirectTo = SUBDOMAIN_DEFAULTS[subdomain] || "/";
  return <Navigate to={redirectTo} replace />;
};
